import { motion } from "framer-motion";
import { Quote, Star, MessageSquare } from "lucide-react";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

const testimonials = [
  {
    id: 1,
    name: "Emmanuel Antwi",
    role: "Content Creator",
    company: "SmartDatafy / SmartTech AI",
    avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=EmmanuelAntwi&backgroundColor=b6e3f4&clothingColor=3c4f5c&hairColor=2c1b18&skinColor=ae5d29&top=shortHairShortFlat",
    color: "from-emerald-500 to-teal-600",
    phone: "0246598202",
    rating: 5,
    text: "Emmanuel built both our data reselling platform and AI platform from the ground up. Fast, reliable, and the quality is exactly what we needed. He understands the vision and delivers without back and forth.",
  },
  {
    id: 2,
    name: "Nii Apiakai Boi",
    role: "Abeka Dzaasetse",
    company: "Abeka Royal Archive",
    avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=NiiApiakaiB&backgroundColor=c0aede&clothingColor=1f3244&hairColor=2c1b18&skinColor=ae5d29&top=shortHairShortWaved",
    color: "from-teal-500 to-cyan-600",
    phone: "0246352907",
    rating: 5,
    text: "He built a digital archive that truly honours our chieftaincy's heritage. The platform is fast, well structured, and easy for our community to use. Emmanuel handled everything professionally and with great care.",
  },
  {
    id: 3,
    name: "Sarah Mitchell",
    role: "Founder",
    company: "Liamma Care (Australia)",
    avatarUrl: "https://api.dicebear.com/7.x/avataaars/svg?seed=SarahMitchell&backgroundColor=ffd5dc&clothingColor=6b4c8c&hairColor=a55728&skinColor=fdbcb4&top=longHairStraight",
    color: "from-violet-500 to-purple-600",
    rating: 4,
    text: "He delivered a fully responsive, accessible website for our home care business. Emmanuel understood our brand immediately and the final product exceeded our expectations. Very professional.",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, duration: 0.6, ease: "easeOut" },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const TestimonialsSection = () => {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01,
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
  });

  return (
    <section
      id="testimonials"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white relative overflow-hidden"
      ref={elementRef}
    >
      {/* Background blobs */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500 rounded-full filter blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-primary-700 rounded-full filter blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isIntersecting ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.span
              className="inline-block text-primary-400 font-medium text-lg mb-4 px-4 py-2 bg-primary-500/10 rounded-full border border-primary-500/20"
              variants={itemVariants}
            >
              Client Testimonials
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              variants={itemVariants}
            >
              What Clients{" "}
              <span className="text-primary-400">
                Say
              </span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Real feedback from people I've built products and solutions for —
              across Ghana, Australia, and beyond.
            </motion.p>
          </motion.div>

          {/* Cards grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={t.id}
                className="group relative flex flex-col gap-5 p-6 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary-500 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                animate={isIntersecting ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.1 + 0.3, duration: 0.55 }}
                whileHover={{ y: -4 }}
              >
                {/* Quote icon */}
                <div className="absolute top-5 right-5 text-primary-500/15 group-hover:text-primary-500/30 transition-colors">
                  <Quote size={34} />
                </div>

                {/* Stars */}
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} size={14} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>

                {/* Quote text */}
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed flex-1">
                  "{t.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <img
                    src={t.avatarUrl}
                    alt={t.name}
                    className="w-10 h-10 rounded-full flex-shrink-0 shadow-lg object-cover bg-gray-100"
                  />
                  <div>
                    <p className="text-gray-900 dark:text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-gray-500 text-xs">
                      {t.role} · {t.company}
                    </p>
                    {t.phone && (
                      <p className="text-gray-400 text-xs mt-0.5">{t.phone}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <motion.div
              className="inline-block p-8 bg-primary-600 rounded-3xl text-white shadow-2xl shadow-primary-500/25"
              whileHover={{ scale: 1.03, y: -4 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold mb-3">
                Ready to be the next success story?
              </h3>
              <p className="text-primary-100 mb-6 max-w-md">
                Let's build something you're proud of. I reply within 24 hours.
              </p>
              <motion.a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-block bg-white text-primary-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Hire Me Now
              </motion.a>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
